import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult, Operation } from '@apollo/client';
import { Kind, OperationTypeNode } from 'graphql';
import { getMainDefinition } from '@apollo/client/utilities';

type SendInput = { roomId: string; messageId?: string | null; clientRequestId?: string | null; [key: string]: unknown };
type Receipt = { version: 1; ids: (string | null)[] };
type Prepared = { input: SendInput[]; finish: (acknowledged: boolean) => void };
type Options = {
  enabled: boolean;
  scope: () => string;
  storage: () => Storage;
  requiresId: (roomId: string, operation: Operation) => Promise<boolean | null | undefined>;
};

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, canonical(item)]));
  }
  return value;
}

const prefix = 'forstok:chat-command:v1:';

// Keep the journal keyed by the original selection, including after routing
// changes. Each image becomes one existing backend command with a stable ID.
function imageCommands(item: SendInput, id: string): SendInput[] {
  if (!Array.isArray(item.imageUrl) || item.imageUrl.length <= 1) return [{ ...item, clientRequestId: id }];
  const fail = () => { throw new Error('Image selection requires at most 10 valid image URLs and one supported caption'); };
  const caption = item.message ?? '';
  if (item.imageUrl.length > 10 || typeof caption !== 'string' || new TextEncoder().encode(caption).length > 1024 ||
      item.replyMessageId != null || item.cartId != null || item.update === true || item.isExpired === true ||
      ['productIds', 'documentUrl', 'videoUrl', 'audioUrl'].some(key => {
        const value = item[key];
        return Array.isArray(value) && value.length > 0;
      })) fail();
  return item.imageUrl.map((value: unknown, index) => {
    const childId = `${id}_image_${index}`;
    if (typeof value !== 'string') return fail();
    if (!/^[A-Za-z0-9_-]{1,256}$/.test(childId) || value.trim() !== value ||
        new TextEncoder().encode(value).length > 2048) fail();
    let url: URL;
    try { url = new URL(value); } catch { return fail(); }
    const authority = /^https:\/\/([^/?#]+)/.exec(value)?.[1];
    if (url.protocol !== 'https:' || url.username || url.password || url.hash || url.port ||
        (authority !== url.hostname && authority !== `${url.hostname}:443`) || value.includes('\\') ||
        [...value].some(character => character.charCodeAt(0) <= 32) || /%(?![0-9a-fA-F]{2})/.test(value) ||
        !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(url.hostname) ||
        /^[0-9.]+$/.test(url.hostname)) fail();
    // Match Go's HTML-safe JSON encoding before any child reaches transport.
    const encoded = JSON.stringify({ approved_image: { url: value, caption } })
      .replace(/[<>&\u2028\u2029]/g, character => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
    if (new TextEncoder().encode(encoded).length > 4096) fail();
    return { ...item, imageUrl: [value], clientRequestId: childId };
  });
}

// Persist only IDs and a digest, never message bodies, tokens or phone numbers.
// An uncertain response retains the IDs across manual retry and page reload.
export class CommandRequestJournal {
  private active = new Set<string>();

  constructor(private storage: () => Storage) {}

  async prepare(scope: string, input: SendInput[], required: boolean[]): Promise<Prepared> {
    if (!scope || required.length !== input.length) throw new Error('Unable to establish the message command scope');
    const data = new TextEncoder().encode(JSON.stringify(canonical([scope, input])));
    const digest = await crypto.subtle.digest('SHA-256', data);
    const key = prefix + Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
    const storage = this.storage();
    const saved = storage.getItem(key);
    let receipt: Receipt | undefined;
    if (saved !== null) {
      receipt = JSON.parse(saved) as Receipt;
      if (receipt.version !== 1 || !Array.isArray(receipt.ids) || receipt.ids.length !== input.length ||
          receipt.ids.some(id => id !== null && (typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(id)))) {
        throw new Error('Cannot safely recover the previous message command');
      }
      if (required.some((value, index) => value && receipt?.ids[index] === null && !input[index].messageId)) {
        throw new Error('Message routing changed while the previous send is unresolved');
      }
    }
    if (!saved && !required.some(Boolean) && !input.some(item => item.clientRequestId != null)) {
      return { input, finish: () => {} };
    }
    if (this.active.has(key)) throw new Error('This message send is already in progress');
    const ids = receipt?.ids ?? input.map((item, index) => item.messageId != null ? null :
      item.clientRequestId ?? (required[index] ? crypto.randomUUID() : null));
    if (ids.some(id => id !== null) && ids.some(id => id === null)) {
      throw new Error('Send command-enabled and ordinary conversations in separate batches');
    }
    const commands = input.flatMap((item, index) => ids[index] === null ? [item] : imageCommands(item, ids[index] as string));
    storage.setItem(key, JSON.stringify({ version: 1, ids }));
    this.active.add(key);
    let finished = false;
    return {
      input: commands,
      finish: (acknowledged: boolean) => {
        if (finished) return;
        finished = true;
        this.active.delete(key);
        // A storage failure must retain the command rather than manufacture a
        // new identity or turn a durable acceptance into a transport exception.
        if (acknowledged) {
          try { storage.removeItem(key); } catch { /* Keep the old ID on retry. */ }
        }
      },
    };
  }
}

// Product decision (19 Oct 2026): video, audio and document replies, with or
// without a caption, are ordinary WhatsApp sends even in command rooms. Any
// image, product or cart content keeps the input on the command path.
function isOrdinaryMedia(item: SendInput): boolean {
  const hasUrl = (value: unknown) => Array.isArray(value) && value.some(url => typeof url === 'string' && url !== '');
  const present = (value: unknown) => Array.isArray(value) ? value.length > 0 : value != null && value !== '';
  return ['videoUrl', 'audioUrl', 'documentUrl'].some(key => hasUrl(item[key])) &&
    !['imageUrl', 'productIds', 'cartId'].some(key => present(item[key]));
}

// Product decision (20 Oct 2026): MBA cannot send dynamic Flows, so a product
// detail or cart summary is an ordinary send in command rooms too. Only a
// product-only or cart-only input qualifies; text, image or media keep it a command.
function isOrdinaryFlow(item: SendInput): boolean {
  const ids = (value: unknown) => Array.isArray(value) && value.some(id => typeof id === 'string' && id !== '');
  const present = (value: unknown) => Array.isArray(value) ? value.length > 0 : value != null && value !== '';
  const product = ids(item.productIds);
  const cart = typeof item.cartId === 'string' && item.cartId !== '';
  return product !== cart &&
    !['message', 'imageUrl', 'videoUrl', 'audioUrl', 'documentUrl'].some(key => present(item[key])) &&
    (product ? !present(item.cartId) : !present(item.productIds));
}

function sendInput(operation: Operation): SendInput[] | null {
  const definition = getMainDefinition(operation.query);
  if (definition.kind !== Kind.OPERATION_DEFINITION || definition.operation !== OperationTypeNode.MUTATION) return null;
  const fields = definition.selectionSet.selections;
  if (fields.length !== 1 || fields[0].kind !== Kind.FIELD || fields[0].name.value !== 'chatSendMessage') return null;
  const argument = fields[0].arguments?.find(item => item.name.value === 'input');
  if (argument?.value.kind !== Kind.VARIABLE || argument.value.name.value !== 'input') {
    throw new Error('Command sends require the existing input variable');
  }
  const input: unknown = operation.variables.input;
  const values: unknown[] = Array.isArray(input) ? input : [input];
  const isInput = (item: unknown): item is SendInput => !!item && typeof item === 'object' &&
    'roomId' in item && typeof item.roomId === 'string';
  if (!values.every(isInput)) throw new Error('Invalid message command input');
  return values;
}

export function createChatCommandLink(options: Options): ApolloLink {
  const journal = new CommandRequestJournal(options.storage);
  return new ApolloLink((operation, forward) => {
    if (!options.enabled) return forward(operation);
    let input: SendInput[] | null;
    try { input = sendInput(operation); } catch (error) {
      return new Observable(observer => observer.error(error));
    }
    if (input === null) return forward(operation);
    const original = input;
    const definition = getMainDefinition(operation.query);
    const field = definition.kind === Kind.OPERATION_DEFINITION ? definition.selectionSet.selections[0] : undefined;
    const resultKey = field?.kind === Kind.FIELD ? field.alias?.value ?? field.name.value : 'chatSendMessage';
    return new Observable(observer => {
      let cancelled = false;
      let prepared: Prepared | undefined;
      let subscription: { unsubscribe: () => void } | undefined;
      const scope = options.scope();
      void (async () => {
        try {
          const policies = new Map<string, boolean>();
          const required: boolean[] = [];
          for (const item of original) {
            // Legacy resend already refers to a durable message, not a new command.
            // Media, product detail and cart summary are never commands; mixed with
            // commands, the journal refuses the batch.
            if (item.messageId != null || isOrdinaryMedia(item) || isOrdinaryFlow(item)) { required.push(false); continue; }
            if (!policies.has(item.roomId)) {
              const value = await options.requiresId(item.roomId, operation);
              if (typeof value !== 'boolean') throw new Error('The backend has not advertised message command support');
              policies.set(item.roomId, value);
            }
            required.push(policies.get(item.roomId) === true);
          }
          if (cancelled) return;
          if (scope !== options.scope()) throw new Error('The signed-in account changed before sending');
          prepared = await journal.prepare(scope, original, required);
          if (cancelled) { prepared.finish(false); return; }
          if (scope !== options.scope()) throw new Error('The signed-in account changed before sending');
          operation.variables = { ...operation.variables, input: prepared.input };
          subscription = forward(operation).subscribe({
            next: (result: FetchResult) => {
              const payload = result.data?.[resultKey] as { success?: boolean } | undefined;
              prepared?.finish(!result.errors?.length && payload?.success === true);
              observer.next(result);
            },
            error: error => { prepared?.finish(false); observer.error(error); },
            complete: () => { prepared?.finish(false); observer.complete(); },
          });
        } catch (error) {
          prepared?.finish(false);
          observer.error(error);
        }
      })();
      return () => { cancelled = true; subscription?.unsubscribe(); prepared?.finish(false); };
    });
  });
}
